import { useCallback, useEffect, useMemo, type Dispatch, type SetStateAction } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { AccountTree } from '@mui/icons-material';
import type {
	ExtractedParam,
	ParameterizedRequest,
} from '@/utils/networkInterceptorParameterization';

type HighlightedUrlProps = {
	url: string;
	runVars: Record<string, string>;
	onUpdateVar: (name: string, value: string) => void;
	extractionPaths: ExtractedParam[];
};

type Props = {
	active: boolean;
	reactFlow: any;
	requests: ParameterizedRequest[];
	allExtracted: ExtractedParam[];
	runVars: Record<string, string>;
	setRunVars: Dispatch<SetStateAction<Record<string, string>>>;
	runResults: Record<
		string,
		{
			ok: boolean;
			status?: number;
			statusText?: string;
			durationMs: number;
			startedAt?: string;
			finishedAt?: string;
			responsePreview?: string;
			error?: string;
		}
	>;
	isHistoryMode: boolean;
	historyEntry?: any | null;
	runExecutedAt?: string | null;
	HighlightedUrl: React.ComponentType<HighlightedUrlProps>;
};

const NODE_WIDTH = 280;
const COLLAPSED_NODE_HEIGHT = 120;
const PARAM_ORANGE = '#f57c00';

type DependencyNodeData = {
	serial: number;
	url: string;
	executedAtIso: string;
	executedAtLabel: string;
	durationMs?: number;
	req: ParameterizedRequest;
	runVars: Record<string, string>;
	allExtracted: ExtractedParam[];
	HighlightedUrl: React.ComponentType<HighlightedUrlProps>;
	onUpdateVar: (name: string, value: string) => void;
	isCollapsed: boolean;
	onToggleCollapse: (nodeId: string) => void;
};

export default function DependencyGraphTab({
	active,
	reactFlow,
	requests,
	allExtracted,
	runVars,
	setRunVars,
	runResults,
	isHistoryMode,
	historyEntry,
	runExecutedAt,
	HighlightedUrl,
}: Props) {
	// Dependency graph state (so nodes are movable and positions persist)
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	if (!reactFlow)
		return (
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					flexDirection: 'column',
					p: 3,
				}}
			>
				<Typography variant="h6">Dependency Graph</Typography>
				<Typography variant="body1">No react flow provided</Typography>
			</Box>
		);

	// @ts-ignore
	const [depNodes, setDepNodes, onDepNodesChange] = reactFlow.useNodesState<any>([]);
	// @ts-ignore
	const [depEdges, setDepEdges, onDepEdgesChange] = reactFlow.useEdgesState<any>([]);
	const toggleNodeCollapse = useCallback(
		(nodeId: string) => {
			setDepNodes((prev: any[]) =>
				prev.map((node) =>
					node.id === nodeId
						? {
								...node,
								data: {
									...node.data,
									isCollapsed: !node.data?.isCollapsed,
								},
						  }
						: node
				)
			);
		},
		[setDepNodes]
	);

	const DependencyGraphNode = useCallback(
		({ id, data }: { id: string; data: DependencyNodeData }) => {
			const req = data.req;
			const badgeStyle = (loc: string) => {
				const meta =
					loc === 'url'
						? {
								color: '#1976d2',
								bg: 'rgba(25, 118, 210, 0.12)',
								border: 'rgba(25, 118, 210, 0.35)',
						  }
						: loc === 'body'
						? {
								color: '#2e7d32',
								bg: 'rgba(46, 125, 50, 0.12)',
								border: 'rgba(46, 125, 50, 0.35)',
						  }
						: loc === 'headers'
						? {
								color: '#6a1b9a',
								bg: 'rgba(106, 27, 154, 0.12)',
								border: 'rgba(106, 27, 154, 0.35)',
						  }
						: {
								color: '#616161',
								bg: 'rgba(97, 97, 97, 0.12)',
								border: 'rgba(97, 97, 97, 0.35)',
						  };
				return {
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					height: 16,
					padding: '0 6px',
					borderRadius: 999,
					fontSize: 9,
					fontWeight: 800,
					letterSpacing: 0.4,
					textTransform: 'uppercase' as const,
					color: meta.color,
					backgroundColor: meta.bg,
					border: `1px solid ${meta.border}`,
					lineHeight: 1,
				};
			};

			return (
				<div
					style={{
						background: '#fff',
						border: '1px solid #a2a2a2',
						borderRadius: 8,
						width: NODE_WIDTH,
						minHeight: data.isCollapsed ? COLLAPSED_NODE_HEIGHT : undefined,
						height: data.isCollapsed ? COLLAPSED_NODE_HEIGHT : undefined,
						padding: 8,
						overflow: 'hidden',
					}}
				>
					<reactFlow.Handle type="target" position={reactFlow.Position.Left} />
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							marginBottom: 6,
							gap: 8,
						}}
					>
						<Box
							sx={{
								backgroundColor: 'primary.main',
								color: 'white',
								borderRadius: '50%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								height: 18,
								minWidth: 18,
								padding: '0 6px',
								fontSize: 11,
								fontWeight: 800,
							}}
							title={`API call order: ${data.serial}`}
						>
							{data.serial}
						</Box>
						<button
							type="button"
							className="nodrag"
							onClick={(event) => {
								event.stopPropagation();
								data.onToggleCollapse(id);
							}}
							style={{
								border: '1px solid #ddd',
								background: '#f9f9f9',
								borderRadius: 6,
								padding: '2px 8px',
								fontSize: 10,
								cursor: 'pointer',
							}}
						>
							{data.isCollapsed ? 'Expand' : 'Collapse'}
						</button>
					</div>
					<div
						style={{
							fontWeight: 600,
							fontSize: 12,
							marginBottom: 4,
							color: '#1976d2',
						}}
					>
						{req.method}
					</div>
					{data.isCollapsed ? (
						<div
							style={{
								fontSize: 11,
								color: '#333',
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							}}
							title={data.url}
						>
							{data.url}
						</div>
					) : (
						<>
							<div
								style={{
									fontSize: 11,
									wordBreak: 'break-word',
									color: '#333',
								}}
							>
								<data.HighlightedUrl
									url={data.url}
									runVars={data.runVars}
									onUpdateVar={data.onUpdateVar}
									extractionPaths={data.allExtracted}
								/>
							</div>
							{(data.executedAtLabel || data.durationMs !== undefined) && (
								<div
									style={{
										fontSize: 10,
										color: '#666',
										display: 'flex',
										alignItems: 'center',
										gap: 8,
										marginTop: 6,
									}}
								>
									{data.executedAtLabel ? (
										<span title={data.executedAtIso || ''}>
											Executed: {data.executedAtLabel}
										</span>
									) : null}
									{data.durationMs !== undefined ? (
										<span>{data.durationMs}ms</span>
									) : null}
								</div>
							)}
							{req.extractedParams.length > 0 && (
								<div
									style={{
										fontSize: 10,
										marginTop: 6,
										color: '#666',
										borderTop: '1px solid #eee',
										paddingTop: 4,
									}}
								>
									Produces: {req.extractedParams.map((p) => p.name).join(', ')}
								</div>
							)}
							{req.usedParams.length > 0 && (
								<div
									style={{
										fontSize: 10,
										marginTop: 4,
										color: '#666',
									}}
								>
									<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
										<div>Uses:</div>
										<div
											style={{
												display: 'flex',
												flexWrap: 'wrap',
												gap: 6,
											}}
										>
											{req.usedParams.map((u) => (
												<span
													key={u.name}
													style={{
														display: 'inline-flex',
														alignItems: 'center',
														gap: 6,
														padding: '2px 6px',
														borderRadius: 6,
														border: '1px solid #eee',
														background: '#fafafa',
													}}
												>
													<span
														style={{
															color: PARAM_ORANGE,
															fontFamily: 'monospace',
															fontWeight: 700,
														}}
													>
														{`{{${u.name}}}`}
													</span>
													<span
														style={{
															display: 'inline-flex',
															gap: 4,
														}}
													>
														{(u.locations || []).map((loc) => (
															<span
																key={`${u.name}-${loc}`}
																style={badgeStyle(loc)}
																title={`Used in ${loc}`}
															>
																{loc === 'headers'
																	? 'HDR'
																	: String(loc).toUpperCase()}
															</span>
														))}
													</span>
												</span>
											))}
										</div>
									</div>
								</div>
							)}
						</>
					)}
					<reactFlow.Handle type="source" position={reactFlow.Position.Right} />
				</div>
			);
		},
		[reactFlow]
	);
	const nodeTypes = useMemo(() => ({ dependencyNode: DependencyGraphNode }), [DependencyGraphNode]);

	// Simple auto-arrangement algorithm for dependency graph
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const autoArrangeNodes = useCallback((nodes: any[], edges: any[]): any[] => {
		// Build adjacency map
		const incomingEdges = new Map<string, number>();
		const outgoingEdges = new Map<string, Set<string>>();

		nodes.forEach((node) => {
			incomingEdges.set(node.id, 0);
			outgoingEdges.set(node.id, new Set());
		});

		edges.forEach((edge) => {
			incomingEdges.set(edge.target, (incomingEdges.get(edge.target) || 0) + 1);
			outgoingEdges.get(edge.source)?.add(edge.target);
		});

		// Topological sort to determine levels
		const levels = new Map<string, number>();
		const queue: string[] = [];

		// Start with nodes that have no incoming edges
		nodes.forEach((node) => {
			if (incomingEdges.get(node.id) === 0) {
				queue.push(node.id);
				levels.set(node.id, 0);
			}
		});

		while (queue.length > 0) {
			const nodeId = queue.shift()!;
			const currentLevel = levels.get(nodeId) || 0;

			outgoingEdges.get(nodeId)?.forEach((targetId) => {
				const remainingIncoming = (incomingEdges.get(targetId) || 0) - 1;
				incomingEdges.set(targetId, remainingIncoming);

				if (remainingIncoming === 0) {
					queue.push(targetId);
					levels.set(targetId, currentLevel + 1);
				}
			});
		}

		// Nodes that weren't reached (cycles or disconnected) get placed at level 0
		nodes.forEach((node) => {
			if (!levels.has(node.id)) {
				levels.set(node.id, 0);
			}
		});

		// Position nodes based on levels
		const levelCounts = new Map<number, number>();
		const arrangedNodes = nodes.map((node) => {
			const level = levels.get(node.id) || 0;
			const countAtLevel = levelCounts.get(level) || 0;
			levelCounts.set(level, countAtLevel + 1);

			return {
				...node,
				position: {
					x: level * 400, // Horizontal spacing between levels
					y: countAtLevel * 200, // Vertical spacing within level
				},
			};
		});

		return arrangedNodes;
	}, []);

	// Build dependency graph for React Flow
	const buildDependencyGraph = useCallback((): { nodes: any[]; edges: any[] } => {
		const nodes: any[] = [];
		const edges: any[] = [];
		const executedAtIso =
			(isHistoryMode ? historyEntry?.response?.executedAt : runExecutedAt) || '';

		// Create nodes for each request
		requests.forEach((req, index) => {
			const serial = index + 1;
			const rr = runResults?.[req.uniqueId];
			const executedAtLabel = executedAtIso ? new Date(executedAtIso).toLocaleString() : '';
			nodes.push({
				id: req.uniqueId,
				type: 'dependencyNode',
				position: { x: 0, y: index * 150 }, // Initial positioning, will be auto-arranged
				data: {
					serial,
					url: req.parameterizedUrl || req.originalUrl || '',
					executedAtIso,
					executedAtLabel,
					durationMs: rr?.durationMs,
					req,
					runVars,
					allExtracted,
					HighlightedUrl,
					onUpdateVar: (name: string, value: string) => {
						setRunVars((prev) => ({
							...prev,
							[name]: value,
						}));
					},
					isCollapsed: true,
					onToggleCollapse: toggleNodeCollapse,
				},
				style: {
					padding: 0,
					border: 'none',
					background: 'transparent',
					width: NODE_WIDTH,
				},
			});
		});

		// Create edges based on parameter dependencies
		requests.forEach((consumerReq) => {
			consumerReq.usedParams.forEach((usedParam) => {
				// Find which request produces this parameter
				const producerParam = allExtracted.find(
					(ep) => ep.name === usedParam.name && ep.enabled
				);

				if (producerParam && producerParam.producerRequestUniqueId) {
					// Check if producer request exists
					const producerExists = requests.some(
						(r) => r.uniqueId === producerParam.producerRequestUniqueId
					);

					if (producerExists) {
						edges.push({
							id: `${producerParam.producerRequestUniqueId}-${consumerReq.uniqueId}-${usedParam.name}`,
							source: producerParam.producerRequestUniqueId,
							target: consumerReq.uniqueId,
							label: `{{${usedParam.name}}}`,
							animated: true,
							style: { stroke: PARAM_ORANGE, strokeWidth: 2 },
							labelStyle: { fill: PARAM_ORANGE, fontSize: 10, fontWeight: 700 },
							labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
						});
					}
				}
			});
		});

		// Auto-arrange nodes using a simple hierarchical layout
		const arrangedNodes = autoArrangeNodes(nodes, edges);

		return { nodes: arrangedNodes, edges };
	}, [
		requests,
		allExtracted,
		runVars,
		runResults,
		isHistoryMode,
		historyEntry,
		runExecutedAt,
		autoArrangeNodes,
		setRunVars,
		HighlightedUrl,
		toggleNodeCollapse,
	]);

	// Build / rebuild dependency graph when entering the tab or when data changes.
	// Preserve any user-moved positions by reusing previous node positions by id.
	useEffect(() => {
		if (!active) return;
		const { nodes, edges } = buildDependencyGraph();
		setDepNodes((prev: any[]) => {
			const prevById = new Map(prev.map((n) => [n.id, n]));
			return nodes.map((n) => {
				const existing = prevById.get(n.id);
				return existing
					? {
							...n,
							position: existing.position,
							data: {
								...n.data,
								isCollapsed: existing.data?.isCollapsed ?? true,
							},
					  }
					: n;
			});
		});
		setDepEdges(edges);
	}, [active, buildDependencyGraph, setDepNodes, setDepEdges]);

	const renderDependencyGraph = () => {
		if (depNodes.length === 0) {
			return (
				<Box
					sx={{
						flex: 1,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexDirection: 'column',
						p: 3,
					}}
				>
					<AccountTree sx={{ fontSize: 64, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
					<Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1 }}>
						No requests to visualize
					</Typography>
					<Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>
						Add some API requests to see the dependency graph
					</Typography>
				</Box>
			);
		}

		return (
			<Box
				sx={{
					flex: 1,
					width: '98%',
					minHeight: 0,
					position: 'relative',
					maxHeight: 'calc(100vh - 200px)',
					overflow: 'auto',
					boxShadow: 3,
					m:2,
					borderRadius: 4,
					border: '1px solid',
					borderColor: 'divider',
				}}
			>
				<div style={{ width: '100%', height: '100%', position: 'absolute' }}>
					<reactFlow.ReactFlow
						nodes={depNodes}
						edges={depEdges}
						nodeTypes={nodeTypes}
						onNodesChange={onDepNodesChange}
						onEdgesChange={onDepEdgesChange}
						nodesDraggable
						fitView
						fitViewOptions={{ padding: 0.2 }}
						attributionPosition="bottom-left"
					>
						<reactFlow.Background />
						<reactFlow.Controls />
					</reactFlow.ReactFlow>
				</div>
			</Box>
		);
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
			{/* Dependency graph header */}
			<Box
				sx={{
					p: 1.5,
					display: 'flex',
					alignItems: 'center',
					gap: 2,
					borderBottom: '1px solid',
					borderColor: 'divider',
				}}
			>
				<Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
					API Dependency Graph
				</Typography>
				<Chip
					size="small"
					label={`${requests.length} APIs`}
					sx={{
						height: 20,
						fontSize: 10,
						color: 'info.main',
					}}
				/>
				<Typography
					variant="caption"
					sx={{ color: 'text.secondary', ml: 'auto', fontSize: 10 }}
				>
					Shows how APIs depend on each other through parameter flow
				</Typography>
			</Box>
			{renderDependencyGraph()}
		</Box>
	);
}
